import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import PortfolioPage from './pages/PortfolioPage';
import ContactPage from './pages/ContactPage';
import AdminPage from './pages/AdminPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import CookieConsentBanner from './components/CookieConsentBanner';
import GoogleAnalyticsTracker from './components/GoogleAnalyticsTracker';
import { getEnabledPages, getSiteConfig } from './services/api';
import { PAGE_DEFAULTS, PAGE_ORDER } from './data/pageDefaults';

export default function App() {
  const [pageConfigs, setPageConfigs] = useState(PAGE_DEFAULTS);
  const [googleAnalyticsTag, setGoogleAnalyticsTag] = useState('');

  const refreshPageConfigs = useCallback(async () => {
    try {
      const pages = await getEnabledPages();
      const enabled = pages.reduce((acc, page) => {
        acc[page.pageKey] = {
          ...(PAGE_DEFAULTS[page.pageKey] || {}),
          ...page,
          content: {
            ...(PAGE_DEFAULTS[page.pageKey]?.content || {}),
            ...(page.content || {}),
          },
        };
        return acc;
      }, {});

      const merged = { ...PAGE_DEFAULTS };
      Object.keys(merged).forEach((key) => {
        if (!enabled[key]) {
          merged[key] = { ...merged[key], isEnabled: false };
        }
      });
      Object.keys(enabled).forEach((key) => {
        merged[key] = { ...merged[key], ...enabled[key], isEnabled: true };
      });

      setPageConfigs(merged);
    } catch {
      setPageConfigs(PAGE_DEFAULTS);
    }
  }, []);

  useEffect(() => {
    refreshPageConfigs();
  }, [refreshPageConfigs]);

  useEffect(() => {
    let mounted = true;

    async function loadSiteConfig() {
      try {
        const config = await getSiteConfig();
        if (mounted) {
          setGoogleAnalyticsTag(config.googleAnalyticsTag || '');
        }
      } catch {
        if (mounted) {
          setGoogleAnalyticsTag('');
        }
      }
    }

    loadSiteConfig();
    return () => {
      mounted = false;
    };
  }, []);

  const visiblePages = useMemo(
    () => PAGE_ORDER.filter((key) => pageConfigs[key]?.isEnabled),
    [pageConfigs],
  );

  const fallbackPath = pageConfigs.home?.isEnabled
    ? '/'
    : (visiblePages[0] ? pageConfigs[visiblePages[0]]?.path : '/admin');

  const navLinks = visiblePages.map((key) => ({
    to: pageConfigs[key]?.path,
    label: pageConfigs[key]?.displayName,
  }));

  const homeContent = pageConfigs.home?.content || PAGE_DEFAULTS.home.content;
  const aboutContent = pageConfigs.about?.content || PAGE_DEFAULTS.about.content;
  const servicesContent = pageConfigs.services?.content || PAGE_DEFAULTS.services.content;
  const workContent = pageConfigs['our-work']?.content || PAGE_DEFAULTS['our-work'].content;
  const contactContent = pageConfigs.contact?.content || PAGE_DEFAULTS.contact.content;

  return (
    <div style={{ background: 'var(--dark)', minHeight: '100vh', color: '#fff' }}>
      <GoogleAnalyticsTracker measurementId={googleAnalyticsTag} />
      <Navbar navLinks={navLinks} />
      <Routes>
        {pageConfigs.home?.isEnabled && <Route path="/" element={<HomePage content={homeContent} />} />}
        {pageConfigs.about?.isEnabled && <Route path="/about" element={<AboutPage content={aboutContent} />} />}
        {pageConfigs.services?.isEnabled && <Route path="/services" element={<ServicesPage content={servicesContent} />} />}
        {pageConfigs['our-work']?.isEnabled && <Route path="/our-work" element={<PortfolioPage content={workContent} />} />}
        {pageConfigs.contact?.isEnabled && <Route path="/contact" element={<ContactPage content={contactContent} />} />}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/admin" element={<AdminPage onConfigUpdated={refreshPageConfigs} />} />
        <Route path="*" element={<Navigate to={fallbackPath} replace />} />
      </Routes>
      <CookieConsentBanner />
      <Footer />
    </div>
  );
}
